import type { ArtDto, BrandArtDto, DefaultArtDto, IntegrationDto, OrgDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorText, queryClient, useApi, type ApiError } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Button, ErrorLine, LoadError, Loading, Page, Section, Tag, TextLink, Title } from '../../components/ui.tsx';
import { isAdmin, useMe } from '../../session.tsx';
import { defaultArtWords } from './defaultArt.ts';

/**
 * Generated art (style guide v2 §13C, §14): the banners painted from where NSB
 * is, which a project shows until it has its own (moved here from
 * Organization, L-14, Jason, 2026-10-07: "this banner stuff should be in art"),
 * and the portal's own art, shown on paper and on sumi as the guide's asset
 * gallery asks. The OpenAI key that paints it is one of the connected accounts
 * in Settings.
 */
export function ArtSettings() {
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const { data: integrations, error } = useApi<IntegrationDto[]>(['integrations'], '/integrations');
  const openai = integrations?.find((i) => i.provider === 'openai');
  const connected = openai?.status === 'connected';
  const { data: brand } = useQuery<BrandArtDto, ApiError>({
    queryKey: ['art', 'brand'],
    queryFn: () => api<BrandArtDto>('/art/brand'),
    // Just connected: the two pieces are being painted; look again until they are there.
    refetchInterval: (q) => (connected && (!q.state.data?.login || !q.state.data?.emptyReward) ? 5000 : false),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useFeedbackRecord(null, null);
  if (error) return <LoadError error={error} />;
  if (!integrations) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: ['art'] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Page wide>
      <Title kicker="Settings">Art</Title>
      <p className="max-w-measure text-ink-2">
        Generated atmosphere: the banners a project shows until its own are painted, and the portal’s own login and reward art. Real NSB work
        is never generated.
      </p>

      <p className="mt-6 flex flex-wrap items-baseline gap-x-3">
        <span className="label">OpenAI</span>
        <span className={`label ${connected ? '!text-moss-deep' : ''}`}>{connected ? 'Connected' : 'Not connected'}</span>
        {openai?.accountLabel ? <span className="text-ink-2">{openai.accountLabel}</span> : null}
        <TextLink to="/settings/connections" className="text-sm">
          {connected ? 'Manage it in Connections' : 'Connect it in Connections'}
        </TextLink>
      </p>
      <ErrorLine error={err} />

      <DefaultBanners admin={admin} />

      <Section title="Portal art">
        <ul className="divide-y divide-rule border-b border-rule">
          <BrandRow
            name="Login"
            note="The right half of the sign-in page."
            art={brand?.login ?? null}
            painting={connected && !brand?.login}
            onRedo={admin && connected ? () => void run(() => api('/art/brand', { body: { kinds: ['login'] } })) : null}
            busy={busy}
          />
          <BrandRow
            name="Nothing waiting"
            note="Above “Nothing is waiting on you. Enjoy the quiet.” on the client home."
            art={brand?.emptyReward ?? null}
            painting={connected && !brand?.emptyReward}
            onRedo={admin && connected ? () => void run(() => api('/art/brand', { body: { kinds: ['empty_reward'] } })) : null}
            busy={busy}
          />
        </ul>
      </Section>
    </Page>
  );
}

/** A row of the page: the piece's name and note, the art as it is used, and its button, the same columns in every section. */
const ROW = 'grid gap-x-6 gap-y-3 py-5 sm:grid-cols-[9rem_1fr_8.5rem] sm:items-start';

function BrandRow(props: { name: string; note: string; art: ArtDto | null; painting: boolean; onRedo: (() => void) | null; busy: boolean }) {
  return (
    <li className={ROW}>
      <div>
        <span className="font-display text-[1.125rem]">{props.name}</span>
        <span className="meta block">{props.note}</span>
        {props.painting ? (
          <span className="mt-1 inline-block">
            <Tag>painting</Tag>
          </span>
        ) : null}
      </div>
      {props.art ? (
        // On paper and on sumi, at desktop and phone width (guide §14 step 10).
        <div className="grid grid-cols-[3fr_1fr] gap-3">
          <img src={`${props.art.url}?w=1024`} alt={props.art.alt} loading="lazy" className="w-full rounded-[2px] bg-paper" />
          <img src={`${props.art.url}?w=640`} alt="" loading="lazy" className="w-full rounded-[2px] bg-sumi" />
        </div>
      ) : (
        <div className="aspect-[16/9] rounded-[2px] bg-inset" />
      )}
      {props.onRedo ? (
        <Button tone="quiet" disabled={props.busy || props.painting} onClick={props.onRedo}>
          Paint again
        </Button>
      ) : (
        <span />
      )}
    </li>
  );
}

/**
 * The organization's own banners (Jason, 2026-10-06): painted from where NSB is, by its town alone, and shown on every
 * project that has none of its own yet, with the strips behind the funnel. Where NSB is is set under Organization.
 */
function DefaultBanners({ admin }: { admin: boolean }) {
  const { data: org, error } = useApi<OrgDto>(['org'], '/org');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const again = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/org/art', { body: {} });
      await queryClient.invalidateQueries({ queryKey: ['org'] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!org && !error) return null;
  return (
    <Section title="Banners where a project has none">
      {org ? <DefaultBannersRow art={org.defaultArt} onPaint={admin ? () => void again() : null} busy={busy} /> : null}
      <ErrorLine error={error ? errorText(error) : err} />
    </Section>
  );
}

/**
 * The banners as a row of the page, as the portal's art is: where they are painted from and how far they are, this
 * month's (or the nearest painted) cropped as it lies behind a project home on a wide screen and on a phone, and
 * Paint them again. Why they cannot be painted yet takes the button's place, with the way to what is missing.
 */
export function DefaultBannersRow({ art, onPaint, busy }: { art: DefaultArtDto; onPaint: (() => void) | null; busy: boolean }) {
  const words = defaultArtWords(art);
  return (
    <>
      <p className="max-w-measure text-ink-2">
        Every project shows a watercolor of its place for the month it is. One with no place yet, and NSB’s own, shows these until its own are
        painted. They are painted from where NSB is, set under <TextLink to="/settings/organization">Organization</TextLink>.
      </p>
      <ul className="mt-2 divide-y divide-rule border-b border-rule">
        <li className={ROW}>
          <div>
            <span className="font-display text-[1.125rem]">{art.place ?? 'No place yet'}</span>
            {words ? <span className="meta block">{words}</span> : null}
            {art.painting ? (
              <span className="mt-1 inline-block">
                <Tag>painting</Tag>
              </span>
            ) : null}
            {art.blocked ? (
              <span className="mt-1 block text-[0.875rem] text-ink-2">
                {art.blocked}{' '}
                {art.blocked.includes('in Settings') ? <TextLink to="/settings/connections">Open Connections</TextLink> : <TextLink to="/settings/organization">Open Organization</TextLink>}
              </span>
            ) : null}
          </div>
          {art.banner ? (
            <div className="grid grid-cols-[3fr_1fr] gap-3">
              <Crop art={art.banner} aspect="aspect-[12/5]" w={1024} />
              <Crop art={art.banner} aspect="aspect-square" decorative />
            </div>
          ) : (
            <div className="aspect-[12/5] rounded-[2px] bg-inset" />
          )}
          {onPaint && !art.blocked ? (
            <Button tone="quiet" disabled={busy || art.painting} onClick={onPaint}>
              {art.months ? 'Paint them again' : 'Paint them'}
            </Button>
          ) : (
            <span />
          )}
        </li>
      </ul>
    </>
  );
}

/** A banner cropped as it lies full-bleed behind a project home: wide on a wide screen, square on a phone. */
function Crop({ art, aspect, w = 640, decorative = false }: { art: ArtDto; aspect: string; w?: number; decorative?: boolean }) {
  return (
    <div className={`overflow-hidden rounded-[2px] bg-inset ${aspect}`}>
      <img src={`${art.url}?w=${w}`} alt={decorative ? '' : art.alt} loading="lazy" className="h-full w-full object-cover" style={{ objectPosition: 'center 30%' }} />
    </div>
  );
}
